// PollForm.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Utilities
import { timestampFromMillis } from 'utilities/DateTime';

// CSS
import './PollForm.css';

// Components
import IconButton from 'components/iconbutton/IconButton';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

/**
 * PollForm
 *
 * Form component for creating or editing poll nodes.
 *
 * @param {object} tempNode - Current poll node (existing or new).
 * @param {Function} handleTempNodeChange - Callback to update tempNode.
 *
 * @returns {ReactNode} PollForm component.
 */
const PollForm = ({ tempNode, height = '320px', handleTempNodeChange }) => {
  const pollinfo = tempNode.pollinfo || {};

  const tempNodeKey = tempNode?.key;
  const tempNodeTitle = tempNode?.title;
  const pollOptions = pollinfo?.options;
  const pollDurationMs = pollinfo?.durationMs;

  // ----- REFS ----- //

  const lastSyncedKeyRef = useRef(null);

  // ----- STATE ----- //

  // --- Local state ---

  const [title, setTitle] = useState(tempNode.title || '');
  const [choices, setChoices] = useState(pollinfo.options || ['', '']);
  const [duration, setDuration] = useState(() => {
    const durationMs = pollinfo.durationMs || 86400000; // 1 day default
    const days = Math.floor(durationMs / 86400000);
    const hours = Math.floor((durationMs / 3600000) % 24);
    const minutes = Math.floor((durationMs / 60000) % 60);
    return { days, hours, minutes };
  });

  // ----- EFFECTS ----- //

  // --- Sync from parent only when node identity changes ---

  useEffect(() => {
    if (lastSyncedKeyRef.current === tempNodeKey) return;
    lastSyncedKeyRef.current = tempNodeKey;

    setTitle(tempNodeTitle || '');

    if (pollOptions) {
      setChoices(pollOptions.length ? pollOptions : ['', '']);
    }

    if (pollDurationMs) {
      const days = Math.floor(pollDurationMs / 86400000);
      const hours = Math.floor((pollDurationMs / 3600000) % 24);
      const minutes = Math.floor((pollDurationMs / 60000) % 60);
      setDuration({ days, hours, minutes });
    }
  }, [tempNodeKey, tempNodeTitle, pollDurationMs, pollOptions]);

  // ----- HANDLERS ----- //

  // --- Handlers ---

  /**
   * handleAddChoice
   *
   * Handles add choice.
   */
  const handleAddChoice = () => {
    if (choices.length < 4) setChoices([...choices, '']);
  };

  /**
   * handleChoiceChange
   *
   * Handles choice change.
   *
   * @param {number} index - index.
   * @param {string} value - value.
   */
  const handleChoiceChange = (index, value) => {
    const updated = [...choices];
    updated[index] = value.slice(0, 25);
    setChoices(updated);
  };

  /**
   * handleDurationChange
   *
   * Handles duration change.
   *
   * @param {object} field - field.
   * @param {string} value - value.
   */
  const handleDurationChange = (field, value) => {
    const updated = { ...duration, [field]: parseInt(value, 10) };
    setDuration(updated);
  };

  // ----- MEMOS ----- //

  // --- Validation ---

  const isValid = useMemo(() => {
    const hasTitle = title.trim().length > 0;
    const validOptions = choices.map(c => c.trim()).filter(Boolean);
    const hasEnoughOptions = validOptions.length >= 2;
    const hasDuration = duration.days + duration.hours + duration.minutes > 0;
    return hasTitle && hasEnoughOptions && hasDuration;
  }, [title, choices, duration]);

  // ----- EFFECTS ----- //

  // --- Push changes up when local state changes ---

  useEffect(() => {
    if (!isValid) return;

    const durationMs =
      (duration.days * 86400000) +
      (duration.hours * 3600000) +
      (duration.minutes * 60000);

    handleTempNodeChange('pollinfo', {
      options: choices.map(c => c.trim()).filter(Boolean),
      durationMs,
      endsAt: timestampFromMillis(Date.now() + durationMs),
      ready: true,
    });

    handleTempNodeChange('title', title.trim());
  }, [title, choices, duration, isValid, handleTempNodeChange]);

  // ----- RENDER ----- //

  // --- Render ---

  return (
    <div className="poll-form-container">

      {/* TITLE */}
      <div className="poll-form-title">
        Poll Settings
      </div>

      <TextInput
        style={{
          height: 'auto',
        }}
        value={title}
        maxLength={100}
        placeholder="Ask a question"
        showCount
        onChange={(e) => setTitle(e.target.value)}
      />

      {/* CHOICES */}
      <div className="poll-form-choices">
        {choices.map((choice, index) => (

          <div className="poll-form-choice-row" key={index}>
            <TextInput
              value={choice}
              maxLength={25}
              placeholder={`Choice ${index + 1}`}
              onChange={(e) => handleChoiceChange(index, e.target.value)}
            />
          </div>
        ))}

        {/* ADD BUTTON CONTAINER */}
        {choices.length < 4 && (

          <div className="poll-form-add-container">
            <IconButton
              iconKey="add"
              iconColor="#FFFFFF"
              size={20}
              onClick={handleAddChoice}
            />
          </div>
        )}
      </div>

      {/* POLL LENGTH SECTION */}
      <div className="poll-form-length-section">

        {/* POLL LENGTH LABEL */}
        <div className="poll-form-label">Poll length</div>

        {/* POLL LENGTH SELECT WRAPPERS */}
        <div className="poll-form-length-selectors">
          <SelectInput
            style={{
              width: '100px',
            }}
            value={duration.days}
            options={Array.from({ length: 8 }, (_, i) => ({
              value: i,
              label: `${i} ${i === 1 ? 'day' : 'days'}`,
            }))}
            onChange={(e) => handleDurationChange('days', e.target.value)}
          />

          <SelectInput
            style={{
              width: '100px',
            }}
            value={duration.hours}
            options={Array.from({ length: 24 }, (_, i) => ({
              value: i,
              label: `${i} hr`,
            }))}
            onChange={(e) => handleDurationChange('hours', e.target.value)}
          />

          <SelectInput
            style={{
              width: '100px',
            }}
            value={duration.minutes}
            options={[0, 5, 10, 15, 30, 45].map((min) => ({
              value: min,
              label: `${min} min`,
            }))}
            onChange={(e) => handleDurationChange('minutes', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

// Dev Tools
PollForm.displayName = 'PollForm';

export default PollForm;
